import { events } from "@constants/tracking";
import { usePurchaseDialog } from "@contexts/DialogContext/dialogs/purchase";
import { useHasAccess } from "@features/dashboard/hooks/useHasAccess";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { getPremiumUpsellCopy } from "@helpers/premium-upsell";
import { useProductDetails } from "@hooks/useProduct";
import { resolveExamDefaultProductType } from "@memorang/helpers/product";
import { canShowSessionReportPremiumCard } from "@memorang/helpers/report";
import type { SessionReportAppletValue } from "@memorang/ui/components/SessionReport/SessionReportApplet";
import { trackCustomEvent } from "analytics";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

type PremiumCard = NonNullable<SessionReportAppletValue["cards"]["premium"]>;

type SessionReportPremiumCardResult = {
	premium?: PremiumCard;
	onUpgrade?: () => void;
};

export const useSessionReportPremiumCard = (
	sessionId?: string,
): SessionReportPremiumCardResult => {
	const { t } = useTranslation();
	const { showPurchase } = usePurchaseDialog();
	const { hasStudyPackPurchaseAccess, isAccessPending } = useHasAccess();
	const {
		currentExamContext: { studyPackCollectionId, examDate },
	} = useExamContext();

	const productType = resolveExamDefaultProductType(studyPackCollectionId);
	const shouldLoadProduct = !isAccessPending && !hasStudyPackPurchaseAccess;
	const { data: product, isSuccess } = useProductDetails(
		productType,
		undefined,
		{ enabled: shouldLoadProduct },
	);

	const imageUrl = product?.icon?.url;
	const features = product?.features;
	const productName = product?.name;
	const productId = product?.logicalId ?? product?.id;

	const canShow = canShowSessionReportPremiumCard({
		isAccessLoading: isAccessPending,
		hasStudyPackPurchaseAccess,
		isProductReady: isSuccess,
		features,
		imageUrl,
		productName,
	});

	const viewedSessionIdRef = useRef<string | undefined>(undefined);

	useEffect(() => {
		if (!canShow || !sessionId || viewedSessionIdRef.current === sessionId) {
			return;
		}
		viewedSessionIdRef.current = sessionId;
		trackCustomEvent({
			eventName: events.sessionReportPremiumCardViewed,
			view: "session_report",
			product_type: productType,
			session_id: sessionId,
			...(productId ? { product_id: productId } : {}),
			...(productName ? { product_name: productName } : {}),
		});
	}, [canShow, productType, productId, productName, sessionId]);

	if (!canShow || !features || !imageUrl || !productName) {
		return {};
	}

	const onUpgrade = () => {
		trackCustomEvent({
			eventName: events.sessionReportPremiumCardUpgradeClicked,
			view: "session_report",
			product_type: productType,
			...(productId ? { product_id: productId } : {}),
			product_name: productName,
			...(sessionId ? { session_id: sessionId } : {}),
		});
		showPurchase({ productType });
	};

	const { title, description } = getPremiumUpsellCopy({
		examDate,
		productName,
	});

	return {
		premium: {
			title,
			description,
			tipText: t("premium.tip"),
			ctaLabel: t("report.premium.upgradeCta"),
			features,
			imageUrl,
		},
		onUpgrade,
	};
};
